import type { Edge as RFEdge, Node as RFNode } from "@xyflow/react";
import { parse, stringify } from "yaml";
import { type DagNode, layoutDag } from "./dag-layout.js";
import type { StepStatus } from "./types.js";

export interface StepNodeData {
	stepId: string;
	stepType: string;
	config: Record<string, unknown>;
	condition?: string;
	timeout?: string;
	retry?: {
		max_attempts: number;
		backoff: "fixed" | "exponential";
		initial_interval?: string;
		max_interval?: string;
	};
	description?: string;
	executionState?: StepStatus;
	[key: string]: unknown;
}

export type TriggerState =
	| { type: "webhook"; path: string }
	| { type: "api" }
	| { type: "manual" }
	| { type: "cron"; schedule: string }
	| { type: "event"; source: string; event_type: string };

export interface WorkflowState {
	id: string | null;
	name: string;
	version: number;
	trigger: TriggerState;
	inputs: Record<string, unknown>;
	nodes: Array<RFNode<StepNodeData>>;
	edges: RFEdge[];
	selectedNodeId: string | null;
	isDirty: boolean;
	onFailure?: { type: string; config: Record<string, unknown> };
}

export function toYaml(state: WorkflowState): string {
	// Build node ID lookup: React Flow node ID → step ID
	const nodeIdToStepId = new Map<string, string>();
	for (const node of state.nodes) {
		nodeIdToStepId.set(node.id, node.data.stepId);
	}

	// Build depends_on from edges
	const dependsOn = new Map<string, string[]>();
	for (const edge of state.edges) {
		const targetStepId = nodeIdToStepId.get(edge.target);
		const sourceStepId = nodeIdToStepId.get(edge.source);
		if (targetStepId && sourceStepId) {
			if (!dependsOn.has(targetStepId)) dependsOn.set(targetStepId, []);
			dependsOn.get(targetStepId)?.push(sourceStepId);
		}
	}

	const steps = state.nodes.map((node) => {
		const step: Record<string, unknown> = {
			id: node.data.stepId,
			type: node.data.stepType,
			config: node.data.config,
		};

		const deps = dependsOn.get(node.data.stepId);
		if (deps && deps.length > 0) step.depends_on = deps;
		if (node.data.condition) step.condition = node.data.condition;
		if (node.data.timeout) step.timeout = node.data.timeout;
		if (node.data.retry) step.retry = node.data.retry;

		return step;
	});

	const definition: Record<string, unknown> = {
		name: state.name,
		version: state.version,
		trigger: state.trigger,
	};

	if (Object.keys(state.inputs).length > 0) definition.inputs = state.inputs;
	definition.steps = steps;
	if (state.onFailure) definition.on_failure = state.onFailure;

	return stringify(definition, { lineWidth: 0 });
}

export function fromDefinition(definition: Record<string, unknown>): WorkflowState {
	const steps = (definition.steps ?? []) as Array<Record<string, unknown>>;

	// Create React Flow nodes
	const stepIdToNodeId = new Map<string, string>();
	const dagNodes: DagNode[] = [];
	const nodes = steps.map((step, i) => {
		const nodeId = `node-${i}`;
		const stepId = step.id as string;
		stepIdToNodeId.set(stepId, nodeId);
		dagNodes.push({ id: stepId, dependsOn: (step.depends_on as string[]) ?? [] });

		return {
			id: nodeId,
			type: "stepNode",
			position: { x: 0, y: 0 },
			data: {
				stepId,
				stepType: step.type as string,
				config: (step.config ?? {}) as Record<string, unknown>,
				condition: step.condition as string | undefined,
				timeout: step.timeout as string | undefined,
				retry: step.retry as StepNodeData["retry"],
			},
		};
	});

	// Layout
	const positions = layoutDag(dagNodes);
	for (const node of nodes) {
		const pos = positions.get(node.data.stepId);
		if (pos) node.position = pos;
	}

	// Create edges from depends_on
	const edges: Array<{ id: string; source: string; target: string }> = [];
	let edgeIndex = 0;
	for (const step of steps) {
		const deps = (step.depends_on as string[]) ?? [];
		const targetNodeId = stepIdToNodeId.get(step.id as string);
		for (const dep of deps) {
			const sourceNodeId = stepIdToNodeId.get(dep);
			if (sourceNodeId && targetNodeId) {
				edges.push({ id: `edge-${edgeIndex++}`, source: sourceNodeId, target: targetNodeId });
			}
		}
	}

	return {
		id: null,
		name: (definition.name as string) ?? "",
		version: (definition.version as number) ?? 1,
		trigger: (definition.trigger ?? { type: "api" }) as TriggerState,
		inputs: (definition.inputs ?? {}) as Record<string, unknown>,
		nodes,
		edges,
		selectedNodeId: null,
		isDirty: false,
		onFailure: definition.on_failure as WorkflowState["onFailure"],
	};
}

export function fromYaml(yamlString: string): WorkflowState {
	const definition = parse(yamlString) as Record<string, unknown>;
	return fromDefinition(definition);
}
